import { push } from 'redux-first-history';
import { StartSelfServiceSession, EndSelfServiceSession } from '@imprivata/self-service-client';
import * as types from '../constants/cidraActions';
import getErrorMessage from '../constants/getMessage';
import {
  USERNAME_PASSWORD, USERNAME_PASSWORD_WEBSSO, SECOND_FACTOR, SELF_SERVICE_END, SELF_SERVICE_START
} from '../constants/contexts';
import { doUpdateDeviceList } from './enrollment';
import {
  cidMobile, cidra, SharedProtocols, sba, piw, StartSessionProtocols
} from '../constants/clientProtocols';
import {
  capabilitiesListToObject, doSetSelfServiceVersion, doStartAuthSessionSuccess,
  setEnterpriseId, setSubscriberId
} from './startSession';
import { logSsf } from '../util/log';
import { startProxDeviceWatch } from './proxCard';


export function doSmsEnrollVerifyStart(phoneNumber) {
  return {
    phoneNumber,
    type: types.SMS_ENROLL_SUCCESS,
  };
}

export function doStartVerifyImprivataId(deviceList = []) {
  return {
    deviceList,
    type: types.START_VERIFY_IMPRIVATA_ID,
  };
}

export function doStartVerifyNumberForImprivataId(matchingNumber, deviceList = []) {
  return {
    matchingNumber,
    deviceList,
    type: types.START_NUMBER_MATCHING_FOR_IMPRIVATA_ID,
  };
}

export function startLoading() {
  return { type: types.START_LOADING, };
}

export function finishLoading() {
  return { type: types.FINISH_LOADING, };
}

export function setServerState(serverState) {
  return {
    type: types.SET_SERVER_STATE,
    serverState,
  };
}

export function doReset() {
  return doResetState(types.RESET);
}

export function doLogout() {
  return doResetState(types.LOGOUT);
}

export function doResetState(resetType) {
  return function(dispatch, getState) {
    const {
      session, sessionService, isHelpdeskVerificationWorkflow,
    } = getState().login;
    if(session != null) {
      session.close();
    }
    dispatch({ type: resetType, });

    // splitting new behavior for cidmobile
    if(sessionService === cidMobile) {
      dispatch(push('/password') );
    }
    else if (sessionService === sba || sessionService === cidra || sessionService === piw) {
      if (isHelpdeskVerificationWorkflow && resetType === types.LOGOUT) {
        dispatch(push('/helpdesk-verification-finish') );
      }
      else {
        dispatch(push('/username') );
      }
    }
    else {
      dispatch(push('/username-password') );
    }
  };
}

// Lighter Web SSO teardown that preserves the applex session so the outer
// host wrapper does not remount CAUI on connectionClosedByServer. Only fires
// on the Web SSO failure path; the non-Web-SSO branches keep the historical
// doLogout()+doLoginFailure() pattern.
export function doWebSsoAuthFlowReset() {
  return function(dispatch) {
    dispatch({ type: types.WEB_SSO_AUTH_FLOW_RESET, });
    dispatch(push('/username') );
  };
}

export function doLoginFailure(errorCode, isWebSsoWorkflowOverride) {
  return function(dispatch, getState) {
    const isWebSsoWorkflow = (typeof isWebSsoWorkflowOverride === 'boolean')
      ? isWebSsoWorkflowOverride
      : getState().login.isWebSsoWorkflow;
    const context = isWebSsoWorkflow ? USERNAME_PASSWORD_WEBSSO : USERNAME_PASSWORD;
    const errorMessage = getErrorMessage(errorCode, context);
    dispatch({
      errorCode,
      errorMessage,
      type: types.LOGIN_FAIL,
      fromUsernameScreen: true,
    });
  };
}

export function doClearLoginError() {
  return { type: types.CLEAR_LOGIN_ERROR, };
}

export function doSecondFactorFailure(errorCode) {
  const errorMessage = getErrorMessage(errorCode, SECOND_FACTOR);
  return {
    errorCode,
    errorMessage,
    type: types.LOGIN_FAIL,
    fromUsernameScreen: false,
  };
}

export function setTempCodeToManual() {
  return { type: types.SET_TEMP_CODE_TO_MANUAL, };
}

export function doStartEnrollSms() {
  return { type: types.START_ENROLL_SMS, };
}

export function doStartEnrollImprivataId() {
  return { type: types.START_ENROLL_IMPRIVATA_ID, };
}

export function completeAuthentication() {
  return { type: types.COMPLETE_AUTHENTICATION, };
}

export function doStartIgnoreTimeout() {
  return { type: types.START_IGNORE_TIMEOUT, };
}

export function doEndIgnoreTimeout() {
  return { type: types.END_IGNORE_TIMEOUT, };
}

export function doSetInterplexAddress(interplexAddress) {
  return {
    type: types.SET_INTERPLEX_ADDRESS,
    interplexAddress: interplexAddress,
  };
}

export function doSetAuthenticationToken(authenticationToken) {
  return {
    type: types.SET_AUTHENTICATION_TOKEN,
    authenticationToken: authenticationToken,
  };
}

export function doSetSelfServiceSessionActive(isActive) {
  return {
    type: types.SET_SELF_SERVICE_SESSION_ACTIVE,
    selfServiceSessionActive: isActive,
  };
}

export function doStartDeviceManagementSession() {
  return function(dispatch, getState) {
    const {
      selfServiceEnvironment,
      oneSignTenantId,
      oneSignUserId,
      authenticationToken,
      selfServiceVersion,
      cloudAuthTenantId,
    } = getState().login;

    // tests ! we should make this a separate funciton to test
    return StartSelfServiceSession(
      selfServiceEnvironment,
      selfServiceVersion,
      authenticationToken,
      oneSignTenantId,
      oneSignUserId,
      cloudAuthTenantId)
      .then(result => {
      // console.log("StartDeviceManagementSession result:", result);
        // Check result for success/error info
        if(!result.ok || result.fail) {
          // console.log("StartDeviceManagementSession failed");
          dispatch(doSelfServiceError(SELF_SERVICE_START) );
          dispatch(doLogout() );
        }
        else {
          dispatch(doSetSelfServiceSessionActive(true) );
          dispatch(doSetAuthenticationToken(result.deviceManagementToken) );
          if(result.deviceList != null) {
            dispatch(doUpdateDeviceList(result.deviceList) );
          }
          dispatch(doSetInterplexAddress(result.interplexAddress) );
        }
      },
      () => {
        // console.log("StartDeviceManagementSession failed, request rejected.");
        dispatch(doSelfServiceError(SELF_SERVICE_END) );
        dispatch(doLogout() );
      });
  };
}

export function doEndDeviceManagementSession() {
  return function(dispatch, getState) {
    const {
      selfServiceEnvironment,
      selfServiceVersion,
      authenticationToken,
      oneSignTenantId,
      oneSignUserId,
      interplexAddress,
      cloudAuthTenantId,
    } = getState().login;

    // tests ! we should make this a separate funciton to test
    return EndSelfServiceSession(
      selfServiceEnvironment,
      selfServiceVersion,
      authenticationToken,
      oneSignTenantId,
      oneSignUserId,
      interplexAddress,
      cloudAuthTenantId)
      .then(result => {
        // console.log("EndDeviceManagementSession result:", result);
        // Check result for success/error info
        if(!result.ok || result.fail) {
          // console.log("EndDeviceManagementSession failed");
          dispatch(doSelfServiceError(SELF_SERVICE_END) );
          dispatch(doLogout() );
        }
        else {
          dispatch(doSetSelfServiceSessionActive(false) );
          dispatch(doSetAuthenticationToken(result.authenticationToken) );
          dispatch(completeAuthentication() );
          console.log('End device management session');
        }
      },
      () => {
        dispatch(doSelfServiceError(SELF_SERVICE_END) );
        dispatch(doLogout() );
      });
  };
}

export function doSelfServiceError(context) {
  const { SharedErrorCodeInvalidCredentials, } = SharedProtocols;
  const errorCode = SharedErrorCodeInvalidCredentials.asString;
  const errorMessage = getErrorMessage(errorCode, context);
  return {
    errorCode,
    errorMessage,
    type: types.ENROLL_RETRY,
  };
}

export function doSetInitialTempCode(showInitialTempCode) {
  return {
    showInitialTempCode,
    type: types.SHOW_INITIAL_TEMPCODE,
  };
}

export function doSetSelfServiceDeviceManagementEnabled(ssdmEnabled) {
  return {
    ssdmEnabled,
    type: types.SET_SELF_SERVICE_DEVICE_MANAGEMENT_ENABLED,
  };
}

export function doSetSelfServiceEnvironment(selfServiceEnvironment) {
  return {
    selfServiceEnvironment,
    type: types.SET_SELF_SERVICE_ENVIRONMENT,
  };
}

export function doStartInitiateIdv() {
  return { type: types.START_INITIATE_IDV, };
}

export function doStartWaitIdvResult() {
  return { type: types.START_WAIT_IDV_RESULT, };
}

export function doSetStateToken(stateToken) {
  return {
    stateToken,
    type: types.SET_STATE_TOKEN,
  };
}

export function doStartAuthSessionAndRestoreAuthState(
  resumeAuthStartedFunction, resumeAuthFunction, restoreRequestContext = null
) {
  return function(dispatch, getState) {
    const { StartAuthSession, StartResponseStartAuthenticationSessionSucceeded, } = StartSessionProtocols;
    const {
      authClient, sessionService, authAppId, authInstanceId = null,
      initialUsername, username, forceMfa = false, optionalWorkflowName = null, requestContext = null,
    } = getState().login;
    dispatch(resumeAuthStartedFunction() );
    // The subscriber name sent here must not be null. Some services (e.g. WebLogin) never
    // populate initialUsername, and the host can set it to undefined when no username hint
    // is present, which the applex Avro converter serializes as null. Fall back to the live
    // username, then to the empty string the initial state uses.
    const subscriberName = initialUsername || username || '';
    // Callers that only start a session to carry a restore pass a request-context fragment
    // saying so. Callers that pass nothing keep the request context untouched, null included.
    const startRequestContext = restoreRequestContext
      ? { ... (requestContext || {}), ... restoreRequestContext, }
      : requestContext;
    const authSessionParams = new StartAuthSession(
      sessionService, authAppId, subscriberName, authInstanceId, forceMfa, null, optionalWorkflowName, null, startRequestContext, null);
    return authClient.sendRequest(authSessionParams).then(responseWithMetadata => {
      const result = responseWithMetadata.message;
      const sourceAddress = responseWithMetadata.metadata.sourceInfo;
      if (result.responseType === StartResponseStartAuthenticationSessionSucceeded) {
        const enterpriseId = result.enterpriseId ? (typeof result.enterpriseId === 'string'? result.enterpriseId : result.enterpriseId.value) : null;
        const subscriberId = result.userId ? (typeof result.userId === 'string'? result.userId : result.userId.value) : null;

        if (enterpriseId !== null && subscriberId !== null) {
          dispatch(setEnterpriseId(enterpriseId) );
          dispatch(setSubscriberId(subscriberId) );
        }
        else {
          logSsf('Enterprise and/or user id null - skipping looking for SSF token');
        }
        dispatch(setServerState(result.authenticationState) );
        const capabilitiesList = result.capabilities;
        const capabilities = capabilitiesListToObject(capabilitiesList);
        const oneSignSessionId = result.sessionId;
        const newAuthClient = authClient.withNewTargetAddress(sourceAddress);
        if(capabilities.DeviceManagement !== undefined) {
          dispatch(doSetSelfServiceVersion('1') );
        }
        dispatch(doStartAuthSessionSuccess(initialUsername, capabilities, oneSignSessionId, newAuthClient) );

        // Start device watch if prox reader is connected
        const currentState = getState().login;
        if (currentState.proxReaderConnected) {
          dispatch(startProxDeviceWatch() );
        }

        dispatch(startLoading() );
        dispatch(resumeAuthFunction() );
      }
      else {
        // got StartAuthenticationSessionFailed
        const isWebSsoWorkflowFlag = getState().login.isWebSsoWorkflow;
        dispatch(setServerState(undefined) );
        dispatch(doLogout() );
        dispatch(doLoginFailure(result.errorCode, isWebSsoWorkflowFlag) );
      }
    }).catch(ex => {
      const isWebSsoWorkflowFlag = getState().login.isWebSsoWorkflow;
      dispatch(doLogout() );
      dispatch(doLoginFailure(ex, isWebSsoWorkflowFlag) );
    });
  };
}
